import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value) || 0);

const getListaMetrics = (lista) => {
  const categorizedItems = (lista.categorias || []).reduce(
    (sum, categoria) => sum + (categoria.itens || []).length,
    0
  );
  const categorizedCompleted = (lista.categorias || []).reduce(
    (sum, categoria) =>
      sum + (categoria.itens || []).filter((item) => item.checked).length,
    0
  );
  const categorizedTotal = (lista.categorias || []).reduce(
    (sum, categoria) =>
      sum +
      (categoria.itens || []).reduce(
        (categoryTotal, item) => categoryTotal + (Number(item.total) || 0),
        0
      ),
    0
  );

  const topLevelItems = lista.itens || [];
  const topLevelCompleted = topLevelItems.filter((item) => item.checked).length;
  const topLevelTotal = topLevelItems.reduce(
    (sum, item) => sum + (Number(item.total) || 0),
    0
  );

  return {
    totalItems: topLevelItems.length + categorizedItems,
    completedItems: topLevelCompleted + categorizedCompleted,
    categoryCount: (lista.categorias || []).length,
    totalValue: topLevelTotal + categorizedTotal,
  };
};

const MinhasListas = () => {
  const [listas, setListas] = useState([]);
  const [nomeLista, setNomeLista] = useState('');
  const { authData } = useContext(AuthContext);
  const { token, userId } = authData;
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [feedback, setFeedback] = useState(() =>
    location.state?.feedbackText
      ? {
          type: location.state.feedbackType || 'info',
          text: location.state.feedbackText,
        }
      : null
  );
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [sharingId, setSharingId] = useState(null);
  const [sharingEmail, setSharingEmail] = useState('');

  useEffect(() => {
    if (location.state?.feedbackText) {
      setFeedback({
        type: location.state.feedbackType || 'info',
        text: location.state.feedbackText,
      });
    }
  }, [location.state]);

  useEffect(() => {
    fetchListas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchListas = async ({ showLoader = true } = {}) => {
    if (showLoader) {
      setLoading(true);
    }

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/listas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setListas(res.data);
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.msg || 'Não foi possível carregar suas listas.',
      });
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  const criarLista = async () => {
    if (nomeLista.trim() === '') {
      setFeedback({
        type: 'error',
        text: 'O nome da lista não pode estar vazio.',
      });
      return;
    }

    setIsCreating(true);
    setFeedback(null);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/listas`,
        { nome: nomeLista },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setNomeLista('');
      await fetchListas({ showLoader: false });
      setFeedback({
        type: 'success',
        text: 'Nova lista criada com sucesso.',
      });
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.msg || 'Não foi possível criar a lista.',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const deletarLista = async (id) => {
    if (!window.confirm('Você tem certeza que deseja deletar esta lista?')) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/listas/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchListas({ showLoader: false });
      setFeedback({
        type: 'success',
        text: 'Lista removida com sucesso.',
      });
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.msg || 'Não foi possível excluir a lista.',
      });
    }
  };

  const updateNameLista = async (id, newName) => {
    if (!newName.trim()) {
      setFeedback({
        type: 'error',
        text: 'O nome da lista não pode estar vazio.',
      });
      return;
    }

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/listas/${id}`,
        { nome: newName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingId(null);
      setEditingName('');
      await fetchListas({ showLoader: false });
      setFeedback({
        type: 'success',
        text: 'Nome da lista atualizado.',
      });
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.msg || 'Não foi possível renomear a lista.',
      });
    }
  };

  const handleEditClick = (lista) => {
    setEditingId(lista._id);
    setEditingName(lista.nome);
    setSharingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleShareClick = (lista) => {
    setSharingId(lista._id);
    setEditingId(null);
    setSharingEmail('');
  };

  const handleCancelShare = () => {
    setSharingId(null);
    setSharingEmail('');
  };

  const handleShare = async () => {
    if (!sharingEmail.trim()) {
      setFeedback({
        type: 'error',
        text: 'O e-mail não pode estar vazio.',
      });
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/listas/${sharingId}/share`,
        { email: sharingEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      handleCancelShare();
      setFeedback({
        type: 'success',
        text: 'Lista compartilhada com sucesso.',
      });
    } catch (err) {
      setFeedback({
        type: 'error',
        text: err.response?.data?.msg || 'Erro ao compartilhar a lista.',
      });
    }
  };

  const isOwner = (lista) => {
    const ownerId =
      typeof lista.usuarioId === 'string' ? lista.usuarioId : lista.usuarioId?._id;

    return ownerId === userId;
  };

  const totalItems = listas.reduce(
    (sum, lista) => sum + getListaMetrics(lista).totalItems,
    0
  );
  const totalCategories = listas.reduce(
    (sum, lista) => sum + getListaMetrics(lista).categoryCount,
    0
  );
  const sharedLists = listas.filter((lista) => !isOwner(lista)).length;

  return (
    <section className="page-section">
      <div className="surface-card page-header-card">
        <div>
          <span className="eyebrow">Painel principal</span>
          <h1 className="section-title">Minhas listas</h1>
          <p className="section-copy">
            Crie listas, acompanhe quantidades, compartilhe com outras pessoas e
            acesse tudo em um painel mais limpo.
          </p>
        </div>
        <div className="stats-grid stats-grid--compact">
          <div className="metric-card">
            <span className="metric-label">Listas</span>
            <strong className="metric-value">{listas.length}</strong>
          </div>
          <div className="metric-card">
            <span className="metric-label">Itens rastreados</span>
            <strong className="metric-value">{totalItems}</strong>
          </div>
          <div className="metric-card">
            <span className="metric-label">Categorias</span>
            <strong className="metric-value">{totalCategories}</strong>
          </div>
          <div className="metric-card">
            <span className="metric-label">Compartilhadas</span>
            <strong className="metric-value">{sharedLists}</strong>
          </div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="surface-card composer-card">
          <div className="section-heading">
            <span className="eyebrow">Nova lista</span>
            <h2 className="section-subtitle">Comece uma compra do zero</h2>
          </div>
          <div className="inline-form">
            <input
              type="text"
              placeholder="Ex.: Compras do fim de semana"
              value={nomeLista}
              onChange={(e) => setNomeLista(e.target.value)}
              className="field-input"
              onKeyDown={(e) => e.key === 'Enter' && criarLista()}
            />
            <button
              className="primary-button"
              type="button"
              onClick={criarLista}
              disabled={isCreating}
            >
              {isCreating ? 'Criando...' : 'Criar lista'}
            </button>
          </div>
        </div>

        <div className="surface-card composer-card composer-card--secondary">
          <div className="section-heading">
            <span className="eyebrow">Melhor uso</span>
            <h2 className="section-subtitle">Dicas para o novo layout</h2>
          </div>
          <p className="section-copy">
            Use categorias para separar setores do mercado e compartilhe apenas as
            listas das quais você é dono.
          </p>
        </div>
      </div>

      {feedback && (
        <div className={`notice notice--${feedback.type}`} role="status">
          {feedback.text}
        </div>
      )}

      {loading ? (
        <div className="surface-card page-status">Carregando suas listas...</div>
      ) : listas.length === 0 ? (
        <div className="surface-card empty-state">
          <i className="bi bi-journal-plus"></i>
          <h2>Você ainda não tem listas</h2>
          <p>
            Crie a primeira lista acima para começar a organizar suas próximas
            compras.
          </p>
        </div>
      ) : (
        <div className="list-card-grid">
          {listas.map((lista) => {
            const metrics = getListaMetrics(lista);
            const owner = isOwner(lista);

            return (
              <article className="surface-card list-card" key={lista._id}>
                <div className="list-card__header">
                  <div>
                    <span
                      className={`status-pill ${
                        owner ? 'status-pill--primary' : 'status-pill--accent'
                      }`}
                    >
                      {owner ? 'Sua lista' : 'Compartilhada'}
                    </span>
                    <h2>{lista.nome}</h2>
                    <p>
                      {metrics.totalItems} itens, {metrics.categoryCount} categorias e{' '}
                      {metrics.completedItems} concluídos.
                    </p>
                  </div>
                  <div className="list-card__price">
                    <span>Total estimado</span>
                    <strong>{formatCurrency(metrics.totalValue)}</strong>
                  </div>
                </div>

                <div className="list-card__metrics">
                  <div>
                    <span>Itens</span>
                    <strong>{metrics.totalItems}</strong>
                  </div>
                  <div>
                    <span>Concluídos</span>
                    <strong>{metrics.completedItems}</strong>
                  </div>
                  <div>
                    <span>Categorias</span>
                    <strong>{metrics.categoryCount}</strong>
                  </div>
                </div>

                <div className="list-card__actions">
                  <Link to={`/lista/${lista._id}`} className="primary-button button-compact">
                    Abrir
                  </Link>
                  {owner && (
                    <button
                      type="button"
                      className="subtle-button button-compact"
                      onClick={() => handleShareClick(lista)}
                    >
                      Compartilhar
                    </button>
                  )}
                  <button
                    type="button"
                    className="subtle-button button-compact"
                    onClick={() => handleEditClick(lista)}
                  >
                    Renomear
                  </button>
                  <button
                    type="button"
                    className="danger-button button-compact"
                    onClick={() => deletarLista(lista._id)}
                  >
                    Excluir
                  </button>
                </div>

                {editingId === lista._id && (
                  <div className="inline-panel">
                    <label className="field">
                      <span className="field-label">Novo nome</span>
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="field-input"
                        onKeyDown={(e) =>
                          e.key === 'Enter' && updateNameLista(lista._id, editingName)
                        }
                      />
                    </label>
                    <div className="inline-actions">
                      <button
                        type="button"
                        className="primary-button button-compact"
                        onClick={() => updateNameLista(lista._id, editingName)}
                      >
                        Salvar
                      </button>
                      <button
                        type="button"
                        className="subtle-button button-compact"
                        onClick={handleCancelEdit}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}

                {sharingId === lista._id && (
                  <div className="inline-panel">
                    <label className="field">
                      <span className="field-label">E-mail para compartilhar</span>
                      <input
                        type="email"
                        placeholder="usuario@email.com"
                        value={sharingEmail}
                        onChange={(e) => setSharingEmail(e.target.value)}
                        className="field-input"
                        onKeyDown={(e) => e.key === 'Enter' && handleShare()}
                      />
                    </label>
                    <div className="inline-actions">
                      <button
                        type="button"
                        className="primary-button button-compact"
                        onClick={handleShare}
                      >
                        Compartilhar
                      </button>
                      <button
                        type="button"
                        className="subtle-button button-compact"
                        onClick={handleCancelShare}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default MinhasListas;
